/* eslint-disable max-lines-per-function */
import { createGlobalState } from 'react-use';
import { useCallback } from 'react';

import { dispatchSnackbarNegativeEvent } from '@dreryk/react-components';
import { createInterpolatedTranslationKey } from '@dreryk/react-common';

import { BackAction, OriginId, ParticipantName, RoomName, Token } from '../types';
import { translationKeys } from '../translations';

import { videoConsultationStorage } from './videoConsultationStorage';

type IdleState = {
   type: 'idle';
};

export type InCallState = {
   type: 'inCall';
   roomName: RoomName;
   token: Token;
   participantName: ParticipantName;
   originId: OriginId;
   window: {
      minimized: boolean;
      locked: boolean;
      backAction?: BackAction;
   };
};

type VideoConsultationGlobalState = IdleState | InCallState;

const useGlobalState = createGlobalState<VideoConsultationGlobalState>({
   type: 'idle',
});

type CallCallback = (data: {
   roomName: RoomName;
   token: Token;
   participantName: ParticipantName;
   originId: OriginId;
}) => void;
type HangUpCallback = VoidFunction;
type LockWindowCallback = VoidFunction;
type UnlockWindowCallback = VoidFunction;
type MinimizeWindowCallback = VoidFunction;
type ShowWindowCallback = VoidFunction;
type SetBackActionCallback = (data: { backAction: BackAction; originId: OriginId }) => void;

type UseVideoConsultationGlobalStateReturnFn = {
   state: VideoConsultationGlobalState;
   call: CallCallback;
   hangUp: HangUpCallback;
   lockWindow: LockWindowCallback;
   unlockWindow: UnlockWindowCallback;
   minimizeWindow: MinimizeWindowCallback;
   showWindow: ShowWindowCallback;
   setBackAction: SetBackActionCallback;
};

export const useVideoConsultationGlobalState = (): UseVideoConsultationGlobalStateReturnFn => {
   const [state, setState] = useGlobalState();

   const call = useCallback<CallCallback>(
      (data) => {
         setState((prevState) => {
            if (prevState.type === 'inCall' && prevState.token !== data.token) {
               dispatchSnackbarNegativeEvent(
                  createInterpolatedTranslationKey(
                     translationKeys.youAreNowHavingConversationWithParticipantEndConversationAndTryAgain,
                     {
                        participantName: prevState.participantName,
                     }
                  )
               );

               return prevState;
            }

            const storedState = videoConsultationStorage.getState();

            return {
               type: 'inCall',
               roomName: data.roomName,
               token: data.token,
               participantName: data.participantName,
               originId: data.originId,
               window: {
                  locked: storedState.locked,
                  minimized: false,
               },
            };
         });
      },
      [setState]
   );

   const hangUp = useCallback(() => {
      setState({
         type: 'idle',
      });
   }, [setState]);

   const setWindowState = useCallback(
      <KEY extends keyof InCallState['window']>(property: KEY, value: InCallState['window'][KEY]): void => {
         return setState((prevState) => {
            if (prevState.type !== 'inCall') {
               return prevState;
            }

            return {
               ...prevState,
               window: {
                  ...prevState.window,
                  [property]: value,
               },
            };
         });
      },
      [setState]
   );

   const unlockWindow = useCallback<UnlockWindowCallback>(() => {
      setWindowState('locked', false);
      videoConsultationStorage.setState({
         locked: false,
      });
   }, [setWindowState]);

   const lockWindow = useCallback<LockWindowCallback>(() => {
      setWindowState('locked', true);
      videoConsultationStorage.setState({
         locked: true,
      });
   }, [setWindowState]);

   const minimizeWindow = useCallback<MinimizeWindowCallback>(() => {
      setWindowState('minimized', true);
   }, [setWindowState]);

   const showWindow = useCallback<ShowWindowCallback>(() => {
      setWindowState('minimized', false);
   }, [setWindowState]);

   const setBackAction = useCallback<SetBackActionCallback>(
      (data) => {
         if (state.type === 'inCall' && state.originId === data.originId) {
            setWindowState('backAction', data.backAction);
         }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [state.type, setWindowState]
   );

   return {
      state: state,
      call: call,
      hangUp: hangUp,
      unlockWindow: unlockWindow,
      lockWindow: lockWindow,
      minimizeWindow: minimizeWindow,
      showWindow: showWindow,
      setBackAction: setBackAction,
   };
};
